import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";

const cards = [
  { letter: "D", title: "Design", desc: "3D, immersive, high-conversion web & mobile interfaces", color: "#00E5FF", gradient: "linear-gradient(135deg, #00E5FF, #7A5CFF, #FF2D9A)" },
  { letter: "E", title: "Engineering", desc: "Scalable, high-performance full-stack systems", color: "#7A5CFF", gradient: "linear-gradient(135deg, #7A5CFF, #FF2D9A, #00E5FF)" },
  { letter: "V", title: "Velocity", desc: "Rapid execution with production-ready delivery", color: "#FF2D9A", gradient: "linear-gradient(135deg, #FF2D9A, #00E5FF, #7A5CFF)" },
  { letter: "A", title: "AI", desc: "Intelligent automation and AI-native systems", color: "#00E5FF", gradient: "linear-gradient(135deg, #00E5FF, #FF2D9A, #7A5CFF)" },
];

const easeOut = [0.22, 1, 0.36, 1];

function useCardTilt() {
  const [tilt, setTilt] = useState({ rx: 0, ry: 0, mx: 50, my: 50, active: false });

  const onMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setTilt({ rx: (y - 0.5) * -10, ry: (x - 0.5) * 10, mx: x * 100, my: y * 100, active: true });
  }, []);

  const onLeave = useCallback(() => {
    setTilt({ rx: 0, ry: 0, mx: 50, my: 50, active: false });
  }, []);

  return { tilt, onMove, onLeave };
}

export default function DevaCore() {
  const [activeIdx, setActiveIdx] = useState(null);
  const [scrollIdx, setScrollIdx] = useState(new Set());
  const cardRefs = useRef([]);
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const labelRef = useRef(null);
  const [revealed, setRevealed] = useState(false);

  // Scroll-based card highlighting
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        setScrollIdx((prev) => {
          const next = new Set(prev);
          entries.forEach((e) => {
            const idx = Number(e.target.dataset.idx);
            if (e.isIntersecting) next.add(idx);
            else next.delete(idx);
          });
          return next;
        });
      },
      { rootMargin: "-35% 0px -35% 0px" }
    );
    cardRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [revealed]);

  // Staggered reveal: video first → label → cards one by one
  useEffect(() => {
    if (!sectionRef.current) return;

    // Pre-hide label and cards
    if (labelRef.current) {
      labelRef.current.style.opacity = "0";
      labelRef.current.style.transform = "translateY(30px)";
    }
    cardRefs.current.forEach((el) => {
      if (el) {
        el.style.opacity = "0";
        el.style.transform = "translateY(50px) scale(0.94)";
        el.style.filter = "blur(6px)";
      }
    });

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Step 1: Video fades in (already has transition on it)
          if (videoRef.current) videoRef.current.style.opacity = "0.25";

          // Step 2: Label appears after 1200ms
          setTimeout(() => {
            if (labelRef.current) {
              labelRef.current.style.transition = "opacity 0.8s cubic-bezier(0.22,1,0.36,1), transform 0.8s cubic-bezier(0.22,1,0.36,1)";
              labelRef.current.style.opacity = "1";
              labelRef.current.style.transform = "translateY(0)";
            }
          }, 1200);

          // Step 3: Cards stagger in one by one after 1800ms
          cardRefs.current.forEach((el, i) => {
            if (!el) return;
            setTimeout(() => {
              el.style.transition = "opacity 1s cubic-bezier(0.22,1,0.36,1), transform 1s cubic-bezier(0.22,1,0.36,1), filter 1s cubic-bezier(0.22,1,0.36,1)";
              el.style.opacity = "1";
              el.style.transform = "translateY(0) scale(1)";
              el.style.filter = "blur(0px)";
            }, 1800 + i * 300);
          });

          setRevealed(true);
          observer.disconnect();
        }
      },
      { rootMargin: "-40px 0px" }
    );
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const isLit = useCallback(
    (i) => activeIdx === i || (activeIdx === null && scrollIdx.has(i)),
    [activeIdx, scrollIdx]
  );
  const isDim = useCallback(
    (i) => activeIdx !== null && activeIdx !== i,
    [activeIdx]
  );

  return (
    <section id="deva" ref={sectionRef} data-testid="deva-section" className="relative py-32 md:py-48 overflow-hidden">
      {/* Background Video - starts hidden, fades in first */}
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        style={{ opacity: 0, mixBlendMode: "lighten", transition: "opacity 1.2s cubic-bezier(0.22,1,0.36,1)" }}
      >
        <source src="https://customer-assets.emergentagent.com/job_deva-command-nexus/artifacts/29r1js3p_VID-20260418-WA0001.mp4" type="video/mp4" />
      </video>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <p ref={labelRef} className="sys-label mb-16 md:mb-24">
          SYS.CORE // D.E.V.A
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6" style={{ perspective: "1000px" }}>
          {cards.map((card, i) => (
            <DevaCard
              key={card.letter}
              card={card}
              i={i}
              cardRefs={cardRefs}
              setActiveIdx={setActiveIdx}
              isLit={isLit(i)}
              isDim={isDim(i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function DevaCard({ card, i, cardRefs, setActiveIdx, isLit, isDim }) {
  const { tilt, onMove, onLeave } = useCardTilt();

  return (
    <motion.div
      ref={(el) => (cardRefs.current[i] = el)}
      data-idx={i}
      data-testid={`deva-card-${card.letter.toLowerCase()}`}
      onMouseEnter={() => setActiveIdx(i)}
      onMouseMove={onMove}
      onMouseLeave={() => { setActiveIdx(null); onLeave(); }}
      className={`tech-frame relative bg-[#0B0F14]/60 backdrop-blur-sm p-10 md:p-14 cursor-pointer ${
        isDim ? "opacity-30" : ""
      }`}
      style={{
        border: "1px solid transparent",
        borderImage: `${card.gradient} 1`,
        boxShadow: isLit
          ? `0 0 25px ${card.color}20, 0 0 50px ${card.color}12, 0 0 80px ${card.color}08`
          : `0 0 20px ${card.color}10, 0 0 40px ${card.color}06`,
        transform: tilt.active
          ? `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg) translateY(-6px) scale(1.01)`
          : "rotateX(0deg) rotateY(0deg) translateY(0px) scale(1)",
        transition: "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.7s ease, box-shadow 0.7s ease",
        transformStyle: "preserve-3d",
      }}
    >
      {/* Ambient glow */}
      <div
        className="absolute -inset-[1px] -z-10 blur-[30px] pointer-events-none"
        style={{
          background: card.gradient,
          opacity: isLit ? 0.3 : 0.1,
          transition: "opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      />

      {/* Cursor spotlight */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: tilt.active
            ? `radial-gradient(circle at ${tilt.mx}% ${tilt.my}%, ${card.color}12, transparent 50%)`
            : "none",
          opacity: tilt.active ? 1 : 0,
          transition: "opacity 0.5s ease",
        }}
      />

      {/* Inner glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: isLit ? 1 : 0,
          background: `radial-gradient(circle at 50% 30%, ${card.color}08, transparent 60%)`,
          transition: "opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      />

      <span
        className="block font-heading text-6xl md:text-8xl font-extralight tracking-tighter mb-8 text-center md:text-left"
        style={{
          color: isLit ? card.color : "rgba(255,255,255,0.18)",
          transition: "color 0.5s ease",
        }}
      >
        {card.letter}
      </span>

      <h3 className="font-heading text-xl md:text-2xl font-light text-white/90 mb-3 md:mb-4 tracking-tight text-center md:text-left pop-click">
        {card.title}
      </h3>

      <p className="font-body text-[15px] md:text-base text-zinc-400 leading-relaxed text-center md:text-left max-w-md mx-auto md:mx-0 pop-click">
        {card.desc}
      </p>
    </motion.div>
  );
}
